import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Skeleton } from '@vtex/shoreline'
import { t } from 'i18next'
import moment, { type Moment } from 'moment'

import Datepicker from '~/components/datepicker'
import HeaderPages from '~/components/HeaderPages'
import PageTitle from '~/components/PageTitle'
import Status from '~/components/Status'
import { history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'
import { useSpendOverview } from '~/modules/retailMedia/hooks/useSpendReports'

import CumulativeDailySection from './elements/CumulativeDailySection'
import DailySpendSection from './elements/DailySpendSection'
import HourlySpendSection from './elements/HourlySpendSection'
import OverviewCards from './elements/PacingCard'
import TotalSpendSection from './elements/TotalSpendSection'

import * as S from './styles'
import pageStyles from './styles.module.css'

const MAX_SPAN_DAYS = 60

const START_PARAM = 'start_date'
const END_PARAM = 'end_date'
const PERIOD_PARAM = 'period'
const VALID_PERIOD = /^\d{4}-\d{2}$/
const DAY_PARAM = 'day'
const VALID_DAY = /^\d{4}-\d{2}-\d{2}$/

export function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() + days)
  const y2 = dt.getFullYear()
  const m2 = String(dt.getMonth() + 1).padStart(2, '0')
  const d2 = String(dt.getDate()).padStart(2, '0')
  return `${y2}-${m2}-${d2}`
}

export function daysBetweenInclusive(start: string, end: string): number {
  const a = new Date(`${start}T12:00:00`).getTime()
  const b = new Date(`${end}T12:00:00`).getTime()
  return Math.round((b - a) / 86400000) + 1
}

export function defaultLast30Days(today: string): {
  start_date: string
  end_date: string
} {
  return { start_date: addDaysIso(today, -29), end_date: today }
}

export function clampDateRange(
  start: string,
  end: string,
  minD: string,
  maxD: string
): { start_date: string; end_date: string } {
  let s = start
  let e = end
  if (s < minD) s = minD
  if (e > maxD) e = maxD
  if (s > e) {
    s = minD
    e = minD
  }
  if (daysBetweenInclusive(s, e) > MAX_SPAN_DAYS) {
    e = addDaysIso(s, MAX_SPAN_DAYS - 1)
    if (e > maxD) {
      e = maxD
      s = addDaysIso(e, -(MAX_SPAN_DAYS - 1))
      if (s < minD) s = minD
    }
  }
  if (s > e) {
    e = s
  }
  return { start_date: s, end_date: e }
}

export function parseUrlRange(
  params: URLSearchParams,
  todayIso: string
): {
  start_date: string
  end_date: string
  hadLegacyPeriod: boolean
} {
  const s = params.get(START_PARAM) ?? ''
  const e = params.get(END_PARAM) ?? ''
  if (VALID_DAY.test(s) && VALID_DAY.test(e) && s <= e) {
    return { start_date: s, end_date: e, hadLegacyPeriod: false }
  }
  const periodFromUrl = params.get(PERIOD_PARAM) ?? ''
  if (VALID_PERIOD.test(periodFromUrl)) {
    const [y, m] = periodFromUrl.split('-').map(Number)
    const lastDay = getLastDayOfMonth(y, m)
    return {
      start_date: `${periodFromUrl}-01`,
      end_date: `${periodFromUrl}-${String(lastDay).padStart(2, '0')}`,
      hadLegacyPeriod: true
    }
  }
  return { ...defaultLast30Days(todayIso), hadLegacyPeriod: false }
}

function pushDay(current: URLSearchParams, day: string) {
  const next = new URLSearchParams(current.toString())
  next.set(DAY_PARAM, day)
  history.push({ search: next.toString() })
}

function replaceDateRange(
  current: URLSearchParams,
  start: string,
  end: string
) {
  const next = new URLSearchParams(current.toString())
  next.set(START_PARAM, start)
  next.set(END_PARAM, end)
  next.delete(PERIOD_PARAM)
  history.replace({ search: next.toString() })
}

/**
 * O nome da campanha e a badge de status, extraídos da página.
 *
 * São três ramos — carregando, com nome, sem nome — mais o status opcional
 * dentro do segundo, e viviam no meio de um componente que já decide período,
 * dia selecionado, permissão e navegação. Fora, a página perde quatro pontos de
 * complexidade e este bloco fica legível sozinho.
 */
const CampaignNameSection = ({
  isPending,
  campaignId,
  campaignName,
  campaignStatus
}: {
  isPending: boolean
  campaignId: string
  campaignName?: string
  campaignStatus?: string
}) => {
  if (isPending) {
    return (
      <div className="d-flex" style={{ marginTop: '4px' }}>
        <Skeleton width="200px" height="21px" />
      </div>
    )
  }

  if (!campaignName) return null

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2"
      style={{ marginTop: '4px' }}
    >
      {campaignStatus ? (
        <div className="lh-1" style={{ fontSize: '.75rem' }}>
          <Status status={campaignStatusBadge(campaignStatus)} />
        </div>
      ) : null}
      <Link
        to={`/retail-media/campaign/view/${campaignId}`}
        className={pageStyles.campaignLink}
        title={campaignName}
      >
        {campaignName}
      </Link>
    </div>
  )
}

const SpendReportsPage = () => {
  const { superAdmin } = useAppInfo()
  const { id } = useParams()
  const params = useQueryParams()
  const from = params.get('from')

  const campaignId = id ?? ''

  const backTo =
    from === 'detail'
      ? `/retail-media/campaign/view/${id}`
      : '/retail-media/campaigns'

  const labelBreadcrumb =
    from === 'detail'
      ? t('rm:spendReport.backToCampaign')
      : t('rm:backToCampaigns')

  const actualToday = useMemo(() => new Date().toISOString().split('T')[0], [])

  const hadExplicitDates = useRef(
    VALID_DAY.test(params.get(START_PARAM) ?? '') &&
      VALID_DAY.test(params.get(END_PARAM) ?? '')
  )

  const searchKey = params.toString()

  const urlRangeRaw = useMemo(
    () => parseUrlRange(params, actualToday),
    // searchKey mirrors query string; avoids unstable URLSearchParams identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchKey, actualToday]
  )

  const urlRange = useMemo(
    () => ({
      start_date: urlRangeRaw.start_date,
      end_date: urlRangeRaw.end_date
    }),
    [urlRangeRaw.start_date, urlRangeRaw.end_date]
  )

  /** Query uses URL range; campaign bounds come from response and clip display/API range below. */
  const { data: overviewData, isPending } = useSpendOverview(
    campaignId,
    urlRange
  )

  const bounds = useMemo(() => {
    if (!overviewData?.campaign_start_at) return null
    const minD = overviewData.campaign_start_at
    const maxD = overviewData.campaign_end_at
      ? addDaysIso(overviewData.campaign_end_at, 30)
      : addDaysIso(actualToday, 30)
    return { minD, maxD }
  }, [
    overviewData?.campaign_start_at,
    overviewData?.campaign_end_at,
    actualToday
  ])

  const dateRange = useMemo(() => {
    if (!bounds) return urlRange
    return clampDateRange(
      urlRange.start_date,
      urlRange.end_date,
      bounds.minD,
      bounds.maxD
    )
  }, [bounds, urlRange])

  const effectiveDateRange = useMemo(() => {
    const startAt = overviewData?.campaign_start_at
    if (
      startAt &&
      startAt > dateRange.start_date &&
      startAt <= dateRange.end_date
    ) {
      return { ...dateRange, start_date: startAt }
    }
    return dateRange
  }, [dateRange, overviewData?.campaign_start_at])

  const overviewReady = Boolean(
    !isPending && bounds && overviewData?.campaign_start_at
  )

  /** Seed URL with start/end when missing (shareable default). */
  useEffect(() => {
    const hasStart = VALID_DAY.test(params.get(START_PARAM) ?? '')
    const hasEnd = VALID_DAY.test(params.get(END_PARAM) ?? '')
    if (hasStart && hasEnd) return
    replaceDateRange(params, urlRangeRaw.start_date, urlRangeRaw.end_date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId])

  /** After overview: sync URL to clamped range and drop legacy period. */
  useEffect(() => {
    if (!bounds) return
    if (!hadExplicitDates.current && overviewData?.campaign_end_at) {
      hadExplicitDates.current = true
      const clamped = clampDateRange(
        bounds.minD,
        overviewData.campaign_end_at,
        bounds.minD,
        bounds.maxD
      )
      replaceDateRange(params, clamped.start_date, clamped.end_date)
      return
    }
    if (!hadExplicitDates.current && !overviewData?.campaign_end_at) {
      const defaultStart = addDaysIso(actualToday, -29)
      if (bounds.minD > defaultStart) {
        hadExplicitDates.current = true
        replaceDateRange(params, bounds.minD, addDaysIso(bounds.minD, 29))
        return
      }
    }
    const sameAsUrl =
      dateRange.start_date === urlRange.start_date &&
      dateRange.end_date === urlRange.end_date
    const dropPeriod = Boolean(params.get(PERIOD_PARAM))
    if (sameAsUrl && !dropPeriod) return
    replaceDateRange(params, dateRange.start_date, dateRange.end_date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bounds?.minD,
    bounds?.maxD,
    overviewData?.campaign_end_at,
    dateRange.start_date,
    dateRange.end_date,
    urlRange.start_date,
    urlRange.end_date
  ])

  const outsideRange = useCallback(
    (day: Moment) => {
      if (!bounds) return false
      const d = day.format('YYYY-MM-DD')
      return d < bounds.minD || d > bounds.maxD
    },
    [bounds]
  )

  const spendPresets = useMemo(() => {
    const end = moment().startOf('day')
    const todayM = end.clone()
    const yesterdayM = end.clone().subtract(1, 'day')
    const last7Start = end.clone().subtract(6, 'day')
    const last30Start = end.clone().subtract(29, 'day')
    return [
      {
        text: t('common:labels.lastXDays', { days: 30 }),
        start: last30Start,
        end: todayM.clone()
      },
      {
        text: t('common:labels.lastXDays', { days: 7 }),
        start: last7Start,
        end: todayM.clone()
      },
      {
        text: t('common:words.yesterday'),
        start: yesterdayM.clone(),
        end: yesterdayM.clone()
      },
      {
        text: t('common:words.today'),
        start: todayM.clone(),
        end: todayM.clone()
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDateRangeChange = useCallback(
    (vals: { startDate?: Moment; endDate?: Moment }) => {
      const { startDate, endDate } = vals
      if (!startDate || !endDate || !bounds) return
      const next = clampDateRange(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        bounds.minD,
        bounds.maxD
      )
      replaceDateRange(params, next.start_date, next.end_date)
    },
    [bounds, params]
  )

  const dayFromUrl = params.get(DAY_PARAM) ?? ''
  const selectedDay = useMemo(() => {
    const effectiveMinDay =
      overviewData?.campaign_start_at ?? effectiveDateRange.start_date
    if (
      VALID_DAY.test(dayFromUrl) &&
      dayFromUrl >= effectiveMinDay &&
      dayFromUrl <= effectiveDateRange.end_date
    ) {
      return dayFromUrl
    }
    let capToday = actualToday
    if (actualToday > effectiveDateRange.end_date)
      capToday = effectiveDateRange.end_date
    if (capToday >= effectiveMinDay) return capToday
    if (effectiveDateRange.end_date >= effectiveMinDay)
      return effectiveDateRange.end_date
    return effectiveMinDay
  }, [
    dayFromUrl,
    effectiveDateRange.start_date,
    effectiveDateRange.end_date,
    actualToday,
    overviewData?.campaign_start_at
  ])

  useEffect(() => {
    if (selectedDay !== dayFromUrl) {
      pushDay(params, selectedDay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay])

  const handleDayChange = useCallback(
    (day: string) => pushDay(params, day),
    [params]
  )

  const campaignNameSection = (
    <CampaignNameSection
      isPending={isPending}
      campaignId={campaignId}
      campaignName={overviewData?.campaign_name}
      campaignStatus={overviewData?.campaign_status}
    />
  )

  return (
    <div>
      <HeaderPages backTo={backTo} labelBreadcrumb={labelBreadcrumb} />

      <PageTitle title={t('rm:spendReport.pageTitle')} />
      <div className="bg-white">
        <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <S.PageHeading>{t('rm:spendReport.pageTitle')}</S.PageHeading>
            {campaignNameSection}
          </div>
          {isPending || !bounds ? (
            <Skeleton width="280px" height="36px" />
          ) : (
            <div className="wrapper-datepicker-header">
              <Datepicker
                isRangeDate
                className="datepicker-header"
                defaultStartDate={moment(dateRange.start_date, 'YYYY-MM-DD')}
                defaultEndDate={moment(dateRange.end_date, 'YYYY-MM-DD')}
                onChange={onDateRangeChange}
                outsideRange={outsideRange}
                maxSpanDays={MAX_SPAN_DAYS}
                presets={spendPresets}
                selectionStart={moment(dateRange.start_date, 'YYYY-MM-DD')}
                selectionEnd={moment(dateRange.end_date, 'YYYY-MM-DD')}
              />
            </div>
          )}
        </div>
        <div className="p-4">
          <S.Section>
            <S.SectionTitle>
              {t('rm:spendReport.section.overview')}
            </S.SectionTitle>
            <OverviewCards campaignId={campaignId} dateRange={dateRange} />
          </S.Section>

          <TotalSpendSection
            campaignId={campaignId}
            dateRange={dateRange}
            overviewReady={overviewReady}
            campaignEndAt={
              overviewData?.campaign_end_at &&
              overviewData.campaign_end_at < actualToday
                ? overviewData.campaign_end_at
                : undefined
            }
          />
          <DailySpendSection
            campaignId={campaignId}
            dateRange={dateRange}
            overviewReady={overviewReady}
            campaignEndAt={
              overviewData?.campaign_end_at &&
              overviewData.campaign_end_at < actualToday
                ? overviewData.campaign_end_at
                : undefined
            }
          />
          {superAdmin && (
            <HourlySpendSection
              campaignId={campaignId}
              selectedDay={selectedDay}
              onDayChange={handleDayChange}
              dateRange={effectiveDateRange}
              overviewReady={overviewReady}
              campaignStartAt={overviewData?.campaign_start_at}
            />
          )}
          <CumulativeDailySection
            campaignId={campaignId}
            selectedDay={selectedDay}
            onDayChange={handleDayChange}
            dateRange={effectiveDateRange}
            overviewReady={overviewReady}
            campaignStartAt={overviewData?.campaign_start_at}
          />
        </div>
      </div>
    </div>
  )
}

export default SpendReportsPage
